import { TrafficRole, type TrafficConfig, type TrafficKind, type Weighted } from '../../config/bus.config';
import type { WorldConfig } from '../../config/world.config';
import { laneCenterX } from '../../level/segments/segmentBuilders';
import type { Aabb } from '../../physics/Aabb';
import { Random } from '../../utils/Random';
import { clamp, DEG_TO_RAD, lerp, smoothstep } from '../../utils/math';

/** Where the rival is in his fight for the road. */
export const RivalPhase = {
  /** Coming up from behind on a lane beside the van. */
  Approach: 'APPROACH',
  /** The van is in his lane in front of him: he rams its tail and tries the other side. */
  Blocked: 'BLOCKED',
  /** Beside the van: he creeps ahead and attacks – swings over and presses against it. */
  Alongside: 'ALONGSIDE',
  /** He got past: he cuts in, brake-checks the van, then drives off. */
  Passed: 'PASSED',
  /** The ride ends: he drops back. */
  Retreat: 'RETREAT',
} as const;
export type RivalPhase = (typeof RivalPhase)[keyof typeof RivalPhase];

/** Something that happened in a step (BusManager pays it out / tells it). */
export const TrafficIncident = {
  /** The rival got past the van. */
  Passed: 'PASSED',
  /** The rival crashed into oncoming traffic. */
  Crashed: 'CRASHED',
  /** An oncoming car swerves into the van's lane – it goes for it. */
  Attack: 'ATTACK',
} as const;
export type TrafficIncident = (typeof TrafficIncident)[keyof typeof TrafficIncident];

/** How far an oncoming car is in its hunt for the van. */
const Hunt = { None: 0, Swerved: 1, Committed: 2, Done: 3 } as const;
type Hunt = (typeof Hunt)[keyof typeof Hunt];

/** Lane of the oncoming traffic (left, as on Austrian roads). */
export const ONCOMING_LANE = 0;
/** A drop below a car larger than this (m) lifts it off – a ramp lip. */
const LIP = 0.06;
/** A crashed car slides sideways at this share of the wreck friction and stops this far beside the road (m). */
const WRECK_SLIDE = 0.6;
const WRECK_BESIDE = 1.2;
/** Braking / accelerating of the traffic (m/s²). */
const BRAKE = 10;
const ACCEL = 6;
/** The rival presses this far past the van's flank (m) – the solid bodies stop him there (keepOut). */
const PRESS = 0.4;
/** He pulls back / staggers sideways at this rate (m/s). */
const RELEASE_RATE = 5;
/** Steepest climb a ramp gives (rise per meter) – caps the climb rate a car carries off a lip (like the van). */
const MAX_CLIMB = 0.25;

export interface TrafficCarPose {
  distance: number;
  x: number;
  y: number;
  /** Spin of a crashed car (rad). */
  spin: number;
}

/** One vehicle of the ride's traffic (gameplay state only – BusTrafficPresenter shows it). */
export class TrafficCar {
  /** Target lane (where it drives or changes to), the lane it came from, the change's progress 0 … 1 and pace (s). */
  lane: number;
  fromLane: number;
  fromX = 0;
  laneT = 1;
  laneDuration = 0;
  speed: number;
  /** Speed it wants to drive (oncoming traffic). */
  readonly cruise: number;
  vy = 0;
  airborne = false;
  /** Blinker −1 left / +1 right / 0 (on during a lane change). */
  signal: -1 | 0 | 1 = 0;
  wrecked = false;
  wreckVx = 0;
  spinRate = 0;
  /** The rival: phase, the side of the van he takes (−1 left, +1 right). */
  phase: RivalPhase = RivalPhase.Approach;
  side: -1 | 1 = 1;
  /** Sideways offset towards the van (m, negative: away) and where it goes. */
  lean = 0;
  leanTarget = 0;
  /** Seconds into the current attack (−1: none) and until the next one. */
  attackT = -1;
  attackTimer = 0;
  /** Hits taken (steered into by the van), seconds of the current stagger, of the brake check after passing. */
  hits = 0;
  staggerT = 0;
  brakeT = 0;
  blockedFor = 0;
  /** Oncoming: its hunt for the van. */
  hunt: Hunt = Hunt.None;
  /** Warned the van (HUD, horn) – once per car. */
  warned = false;
  /** Beside the van (lengthwise overlapping) in the last step – it stops at its flank instead of driving into it. */
  beside = false;
  readonly pose: TrafficCarPose;
  readonly previous: TrafficCarPose;

  constructor(
    readonly id: number,
    readonly kind: TrafficKind,
    readonly role: TrafficRole,
    /** +1 with the van, −1 oncoming. */
    readonly dir: 1 | -1,
    /** Index into TrafficKindConfig.paints, −1: as modelled. */
    readonly paint: number,
    readonly width: number,
    readonly length: number,
    readonly height: number,
    distance: number,
    x: number,
    lane: number,
    speed: number,
  ) {
    this.lane = lane;
    this.fromLane = lane;
    this.fromX = x;
    this.speed = speed;
    this.cruise = speed;
    this.pose = { distance, x, y: 0, spin: 0 };
    this.previous = { ...this.pose };
  }

  /** Lane it is in now (nearest lane centre). */
  get currentLane(): number {
    return this.laneT >= 0.5 ? this.lane : this.fromLane;
  }

  /** The rival is still after the van (not past, not dropping back, not crashed). */
  get chasing(): boolean {
    return this.role === TrafficRole.Rival && !this.wrecked && (this.phase === RivalPhase.Approach || this.phase === RivalPhase.Blocked || this.phase === RivalPhase.Alongside);
  }

  /** The rival presses against the van right now (an attack, not staggering). */
  get pressing(): boolean {
    return this.attackT >= 0 && this.staggerT <= 0;
  }
}

/** What the traffic needs to know about the van and the road each step. */
export interface TrafficView {
  /** Track distance of the van's origin, its lateral position, half width and sideways speed, forward speed and lane. */
  readonly busDistance: number;
  readonly busX: number;
  readonly busHalfWidth: number;
  readonly busLateral: number;
  readonly busSpeed: number;
  readonly busLane: number;
  /** The van's nose ahead of / tail behind its origin (m). */
  readonly busFront: number;
  readonly busRear: number;
  /** Ride progress 0 … 1 (more oncoming traffic later). */
  readonly share: number;
  /** No new cars (getting in, the finale) – the rival drops back. */
  readonly calm: boolean;
  /** End of the committed track (an undecided crossing): nothing is spawned behind it, cars ahead turn off there. */
  readonly roadEnd: number;
}

export interface BusTrafficDeps {
  readonly config: TrafficConfig;
  readonly world: WorldConfig;
  /** Ground height at a track position (ramps), null where there is none. */
  readonly groundHeightAt: (distance: number, lateral: number) => number | null;
}

/**
 * The traffic of a ructec-Bulli ride – a fight for the road (user 2026-10-05: "das andere Auto überholt einen von hinten
 * und man muss es wegdrängen … wesentlich aggressiver … ein echter Kampf um den Platz"):
 *
 *   rival      one at a time (RivalConfig), the next 1–2 s after the last: from behind on a lane beside the van,
 *              alongside he attacks again and again – swings over and presses (BusManager: pressed long enough the van
 *              is shoved a lane over, at the road's edge it tips). Steered into he takes a hit and staggers; after
 *              `toughness` hits he flies off the road. Blocked behind the van he rams its tail and tries the other side;
 *              once past he cuts in and brake-checks.
 *   oncoming   now and then on lane 0 – and it goes for the van: it swerves into its lane, follows it once more, then
 *              it is committed (dodge late).
 *
 * Solid bodies: no car drives into the van (keepOut) – a touch is a crash.
 */
export class BusTraffic {
  readonly cars: TrafficCar[] = [];
  private rng = new Random(1);
  private nextId = 1;
  private oncomingTimer = 0;
  private rivalTimer = 0;
  private readonly freshWarnings: TrafficCar[] = [];
  private readonly happened: { readonly incident: TrafficIncident; readonly car: TrafficCar }[] = [];

  constructor(private readonly deps: BusTrafficDeps) {}

  /** A new ride: no cars; the first rival comes soon, oncoming traffic a little later. */
  start(seed: number): void {
    this.cars.length = 0;
    this.rng = new Random(seed);
    this.rivalTimer = this.deps.config.rival.firstAfter;
    this.oncomingTimer = 8;
  }

  clear(): void {
    this.cars.length = 0;
  }

  step(dt: number, view: TrafficView): void {
    this.freshWarnings.length = 0;
    this.happened.length = 0;
    this.spawn(dt, view);
    for (const car of this.cars) this.move(car, dt, view);
    this.rivalMeetsOncoming();
    this.despawn(view);
  }

  /** The chasing rival, or null. */
  get rival(): TrafficCar | null {
    return this.cars.find((car) => car.chasing) ?? null;
  }

  /** The first car whose box meets `box` (the van), not wrecked – or null. */
  contact(box: Readonly<Aabb>): TrafficCar | null {
    for (const car of this.cars) {
      if (!car.wrecked && this.overlaps(car, box.minX, box.maxX, box.minZ, box.maxZ, box.minY, box.maxY)) return car;
    }
    return null;
  }

  /** The car crashed: it spins off towards the road side away from `fromX` and rolls to a stop beside the road. */
  wreck(car: TrafficCar, fromX: number): void {
    const { wreck } = this.deps.config;
    const side = Math.sign(car.pose.x - fromX) || (this.rng.chance(0.5) ? 1 : -1);
    car.wrecked = true;
    car.signal = 0;
    car.attackT = -1;
    car.lean = car.leanTarget = 0;
    const edge = this.deps.world.trackWidth / 2 + WRECK_BESIDE;
    const toEdge = Math.max(0, side > 0 ? edge - car.pose.x : car.pose.x + edge);
    car.wreckVx = side * Math.max(wreck.push, Math.sqrt(2 * wreck.friction * WRECK_SLIDE * toEdge));
    car.spinRate = side * car.dir * wreck.spin * DEG_TO_RAD;
    car.speed *= car.dir < 0 ? 0.3 : 0.6;
  }

  /**
   * The van steered into the rival: he takes a hit – staggers away and slows, or, at RivalConfig.toughness, flies off
   * the road. True when he is out.
   */
  hit(car: TrafficCar, fromX: number): boolean {
    const r = this.deps.config.rival;
    car.hits++;
    if (car.hits >= r.toughness) {
      this.wreck(car, fromX);
      return true;
    }
    car.attackT = -1;
    car.attackTimer = this.rng.range(r.attack.every[0], r.attack.every[1]);
    car.staggerT = r.stagger.seconds;
    car.leanTarget = -r.stagger.away;
    car.speed = Math.max(0, car.speed - r.stagger.slow);
    return false;
  }

  /** The rival's attack is over (he shoved the van, or it rammed him from behind): he pulls back and comes again. */
  endAttack(car: TrafficCar): void {
    const r = this.deps.config.rival;
    car.attackT = -1;
    car.leanTarget = 0;
    car.attackTimer = this.rng.range(r.attack.every[0], r.attack.every[1]);
  }

  /** The rival has won (the van tipped over): he drives off – no brake check, no penalty of his own. */
  release(car: TrafficCar): void {
    car.phase = RivalPhase.Passed;
    car.attackT = -1;
    car.leanTarget = 0;
    car.brakeT = 0;
  }

  /** A rear-end bump: the car is knocked forward a little (it was braking in front of the van, or rammed its tail). */
  bump(car: TrafficCar, view: TrafficView): void {
    car.speed = car.pose.distance > view.busDistance ? view.busSpeed + 4 : Math.max(0, view.busSpeed - 3);
    car.brakeT = 0;
  }

  /** Warning per lane (0 … 1, 1 = there now) of the rival coming from behind, written into `out`. */
  warnings(view: TrafficView, out: number[]): number[] {
    out.fill(0);
    const { warnSeconds } = this.deps.config;
    for (const car of this.cars) {
      const t = this.secondsToReach(car, view);
      if (t === null || t > warnSeconds) continue;
      const lane = car.currentLane;
      out[lane] = Math.max(out[lane] ?? 0, 1 - t / warnSeconds);
    }
    return out;
  }

  /** Cars that started to threaten the van in this step (once per car – horn). */
  get newWarnings(): readonly TrafficCar[] {
    return this.freshWarnings;
  }

  /** What happened in this step. */
  get incidents(): readonly { readonly incident: TrafficIncident; readonly car: TrafficCar }[] {
    return this.happened;
  }

  // ── Spawning ─────────────────────────────────────────────────────────────

  private spawn(dt: number, view: TrafficView): void {
    const c = this.deps.config;
    this.oncomingTimer -= dt;
    if (this.oncomingTimer <= 0 && !view.calm) {
      const placed = this.spawnOncoming(view);
      this.oncomingTimer = placed ? lerp(c.oncoming.every[0], c.oncoming.every[1], view.share) * this.rng.range(0.75, 1.25) : 0.5;
    }
    if (this.cars.some((car) => car.role === TrafficRole.Rival && (car.chasing || car.phase === RivalPhase.Passed))) return;
    this.rivalTimer -= dt;
    if (this.rivalTimer > 0 || view.calm) return;
    this.rivalTimer = this.spawnRival(view) ? this.rng.range(c.rival.between[0], c.rival.between[1]) : 0.3;
  }

  private spawnOncoming(view: TrafficView): boolean {
    const c = this.deps.config.oncoming;
    const distance = Math.min(view.busDistance + c.ahead, view.roadEnd - 12);
    if (distance - view.busDistance < 70) return false; // a crossing right ahead: nothing comes out of it
    const kind = this.pick(c.kinds);
    const speed = this.rng.range(c.speed[0], c.speed[1]);
    const meet = (distance - view.busDistance) / (view.busSpeed + speed);
    if (!this.spotFree(ONCOMING_LANE, distance, kind) || !this.leavesEscape(ONCOMING_LANE, view, meet)) return false;
    // any colour but the rival's (the first)
    const paints = this.deps.config.kinds[kind].paints.length;
    this.add(kind, TrafficRole.Oncoming, -1, distance, ONCOMING_LANE, speed, paints > 1 ? this.rng.int(1, paints - 1) : undefined);
    return true;
  }

  private spawnRival(view: TrafficView): boolean {
    const r = this.deps.config.rival;
    const sides = ([-1, 1] as const).filter((s) => this.validLane(view.busLane + s));
    const preferred = sides[this.rng.int(0, sides.length - 1)]!;
    const distance = view.busDistance - r.behind;
    // not into the oncoming traffic right away
    const side = [preferred, -preferred as -1 | 1].find(
      (s) => this.validLane(view.busLane + s) && !(view.busLane + s === ONCOMING_LANE && this.cars.some((o) => o.dir < 0 && Math.abs(o.pose.distance - distance) < 60)),
    );
    if (side === undefined) return false;
    const lane = view.busLane + side;
    if (!this.spotFree(lane, distance, r.kind)) return false;
    const car = this.add(r.kind, TrafficRole.Rival, 1, distance, lane, view.busSpeed + r.approach, r.paint);
    car.side = side;
    car.phase = RivalPhase.Approach;
    return true;
  }

  private add(kind: TrafficKind, role: TrafficRole, dir: 1 | -1, distance: number, lane: number, speed: number, paint?: number): TrafficCar {
    const k = this.deps.config.kinds[kind];
    const colour = paint ?? (k.paints.length > 0 ? this.rng.int(0, k.paints.length - 1) : -1);
    const x = laneCenterX(this.deps.world, lane);
    const car = new TrafficCar(this.nextId++, kind, role, dir, colour, k.width, k.length, k.height, distance, x, lane, speed);
    car.laneDuration = this.deps.config.laneChangeDuration;
    car.pose.y = car.previous.y = this.ground(distance, x);
    this.cars.push(car);
    return car;
  }

  /** No car in `lane` near `distance`. */
  private spotFree(lane: number, distance: number, kind: TrafficKind): boolean {
    const half = this.deps.config.kinds[kind].length / 2 + 4;
    return !this.cars.some((o) => !o.wrecked && (o.lane === lane || o.currentLane === lane) && Math.abs(o.pose.distance - distance) < half + o.length / 2);
  }

  /** Fairness: when a new car in `lane` meets the van after `seconds`, at least one lane around the van is free then. */
  private leavesEscape(lane: number, view: TrafficView, seconds: number): boolean {
    const busAt = view.busDistance + view.busSpeed * seconds;
    const window = this.deps.config.escapeWindow;
    const blocked = new Set<number>([lane]);
    for (const o of this.cars) {
      if (o.wrecked) continue;
      const at = o.pose.distance + o.dir * o.speed * seconds;
      if (Math.abs(at - busAt) < window + o.length / 2) blocked.add(o.lane);
    }
    return blocked.size < this.deps.world.laneCount;
  }

  private pick(kinds: Weighted<TrafficKind>): TrafficKind {
    const total = kinds.reduce((sum, [, w]) => sum + w, 0);
    let roll = this.rng.range(0, total);
    for (const [kind, weight] of kinds) {
      roll -= weight;
      if (roll <= 0) return kind;
    }
    return kinds[kinds.length - 1]![0];
  }

  private validLane(lane: number): boolean {
    return lane >= 0 && lane < this.deps.world.laneCount;
  }

  // ── Driving ──────────────────────────────────────────────────────────────

  private move(car: TrafficCar, dt: number, view: TrafficView): void {
    Object.assign(car.previous, car.pose);
    const c = this.deps.config;
    const p = car.pose;
    if (car.wrecked) {
      car.speed = Math.max(0, car.speed - c.wreck.friction * dt);
      car.wreckVx -= Math.sign(car.wreckVx) * Math.min(Math.abs(car.wreckVx), c.wreck.friction * WRECK_SLIDE * dt);
      car.spinRate *= Math.max(0, 1 - 1.5 * dt);
      const edge = this.deps.world.trackWidth / 2 + WRECK_BESIDE + 0.2;
      p.x = clamp(p.x + car.wreckVx * dt, -edge, edge);
      p.spin += car.spinRate * dt;
      p.distance += car.dir * car.speed * dt;
      this.vertical(car, dt);
      return;
    }

    const target = car.role === TrafficRole.Rival ? this.driveRival(car, dt, view) : this.driveOncoming(car, view);
    const rate = target > car.speed ? ACCEL : BRAKE;
    car.speed = Math.max(0, car.speed + clamp(target - car.speed, -rate * dt, rate * dt));

    // lateral: the lane (changing over its pace) plus the rival's lean towards the van (or away when staggering)
    if (car.laneT < 1) {
      car.laneT = Math.min(1, car.laneT + dt / car.laneDuration);
      if (car.laneT >= 1) car.signal = 0;
    }
    const laneX = lerp(car.fromX, laneCenterX(this.deps.world, car.lane), smoothstep(car.laneT));
    const leanRate = car.leanTarget > car.lean ? c.rival.attack.leanRate : RELEASE_RATE;
    car.lean += clamp(car.leanTarget - car.lean, -leanRate * dt, leanRate * dt);
    p.x = laneX + car.lean * (Math.sign(view.busX - laneX) || -car.side);
    p.distance += car.dir * car.speed * dt;
    this.keepOut(car, view);
    this.vertical(car, dt);

    // the rival comes up behind the van: warn once (HUD, horn)
    if (!car.warned) {
      const t = this.secondsToReach(car, view);
      if (t !== null && t <= c.warnSeconds) {
        car.warned = true;
        this.freshWarnings.push(car);
      }
    }
  }

  /**
   * Solid bodies (user 2026-10-05: "sie sollen nicht ineinander fahren"): a car that was already beside the van stops at
   * its flank, a car of its direction coming up behind it in its lane stops at its tail – the touch is the crash
   * (BusManager). Only a head-on meeting overlaps for the one step it takes to crash.
   */
  private keepOut(car: TrafficCar, view: TrafficView): void {
    const p = car.pose;
    const halfSum = car.width / 2 + view.busHalfWidth;
    const busNose = view.busDistance + view.busFront;
    const busTail = view.busDistance - view.busRear;
    const besideNow = p.distance + car.length / 2 > busTail && p.distance - car.length / 2 < busNose;
    const dx = p.x - view.busX;
    if (besideNow && car.beside && Math.abs(dx) < halfSum) {
      const side = Math.sign(car.previous.x - view.busX) || Math.sign(dx) || 1;
      p.x = view.busX + side * halfSum;
    } else if (besideNow && !car.beside && car.dir > 0 && Math.abs(dx) < halfSum) {
      // in the van's lane: behind it he stops at its tail, ahead of it (brake check) the van runs into him
      if (car.previous.distance < view.busDistance) {
        p.distance = busTail - car.length / 2;
        car.speed = Math.min(car.speed, view.busSpeed);
      } else {
        p.distance = busNose + car.length / 2;
      }
    }
    car.beside = p.distance + car.length / 2 > busTail && p.distance - car.length / 2 < busNose;
  }

  /** Oncoming traffic: its own speed – and it goes for the van (OncomingConfig.hunt). */
  private driveOncoming(car: TrafficCar, view: TrafficView): number {
    const h = this.deps.config.oncoming.hunt;
    const gap = car.pose.distance - car.length / 2 - (view.busDistance + view.busFront);
    if (car.hunt === Hunt.None && gap < h.from && gap > h.lunge) {
      car.hunt = Hunt.Swerved;
      if (view.busLane !== car.lane) this.beginLane(car, view.busLane, h.laneChange);
      this.happened.push({ incident: TrafficIncident.Attack, car });
    } else if (car.hunt === Hunt.Swerved && gap <= h.lunge) {
      // one last lunge after the van – then it is committed
      car.hunt = Hunt.Committed;
      if (view.busLane !== car.lane) this.beginLane(car, view.busLane, h.laneChange);
    } else if (car.hunt === Hunt.Committed && gap < -(car.length + view.busFront + view.busRear)) {
      car.hunt = Hunt.Done;
      this.beginLane(car, ONCOMING_LANE, h.laneChange * 1.5);
    }
    return this.follow(car);
  }

  /** The gap behind a slower car ahead in its lane (oncoming traffic among itself). */
  private follow(car: TrafficCar): number {
    const gap = this.deps.config.followGap;
    let target = car.cruise;
    for (const o of this.cars) {
      if (o === car || o.wrecked || o.dir !== car.dir || o.lane !== car.lane) continue;
      const ahead = (o.pose.distance - car.pose.distance) * car.dir - (o.length + car.length) / 2;
      if (ahead > 0 && ahead < gap + car.speed * 0.6) target = Math.min(target, ahead < gap * 0.5 ? 0 : o.speed);
    }
    return target;
  }

  /** The rival's fight (RivalConfig) – returns his target speed, changes lanes and attacks as he sees fit. */
  private driveRival(car: TrafficCar, dt: number, view: TrafficView): number {
    const r = this.deps.config.rival;
    const nose = car.pose.distance + car.length / 2;
    const tail = car.pose.distance - car.length / 2;
    const busNose = view.busDistance + view.busFront;
    const busTail = view.busDistance - view.busRear;
    if (view.calm && car.chasing) {
      car.phase = RivalPhase.Retreat;
      car.attackT = -1;
      car.leanTarget = 0;
    }
    if (!this.validLane(view.busLane + car.side)) car.side = car.side > 0 ? -1 : 1;
    const beside = view.busLane + car.side;
    if (car.staggerT > 0) {
      car.staggerT -= dt;
      if (car.staggerT <= 0) car.leanTarget = 0;
    }

    switch (car.phase) {
      case RivalPhase.Approach:
      case RivalPhase.Blocked: {
        if (car.lane === view.busLane && nose <= busTail + 0.5) {
          // the van is in his lane in front of him: he rams its tail – and tries the other side
          car.phase = RivalPhase.Blocked;
          car.blockedFor += dt;
          if (car.blockedFor >= r.switchAfter && car.laneT >= 1) {
            const other = ([-car.side as -1 | 1, car.side] as const).find((s) => this.validLane(view.busLane + s));
            if (other !== undefined) {
              car.side = other;
              this.beginLane(car, view.busLane + other);
            }
            car.blockedFor = 0;
          }
          return view.busSpeed + 2;
        }
        car.phase = RivalPhase.Approach;
        car.blockedFor = 0;
        if (car.lane !== beside && car.laneT >= 1) this.beginLane(car, beside);
        if (nose > busTail && car.lane === beside) {
          car.phase = RivalPhase.Alongside;
          car.attackTimer = this.rng.range(r.attack.every[0], r.attack.every[1]) * 0.5;
        }
        return view.busSpeed + r.approach;
      }
      case RivalPhase.Alongside: {
        if (tail > busNose + r.passedAhead) {
          // past the van: he cuts in and brake-checks it
          car.phase = RivalPhase.Passed;
          car.attackT = -1;
          car.leanTarget = 0;
          car.brakeT = r.passBrake.seconds;
          if (car.lane !== view.busLane && this.laneFree(view.busLane, car.pose.distance, car)) this.beginLane(car, view.busLane);
          this.happened.push({ incident: TrafficIncident.Passed, car });
          return view.busSpeed;
        }
        // the van moved into his lane behind him: back to ramming its tail; it moved away: after it
        if (car.lane === view.busLane && car.laneT >= 1 && nose <= busTail + 0.5) car.phase = RivalPhase.Blocked;
        else if (car.lane !== beside && car.laneT >= 1 && car.attackT < 0) this.beginLane(car, beside);
        if (car.staggerT > 0) return view.busSpeed + r.creep - r.stagger.slow;
        if (car.attackT >= 0) {
          car.attackT += dt;
          const reach = Math.abs(view.busX - laneCenterX(this.deps.world, car.lane)) - (car.width / 2 + view.busHalfWidth);
          car.leanTarget = Math.max(0, reach) + PRESS;
          if (car.attackT >= r.attack.hold + reach / r.attack.leanRate) this.endAttack(car);
        } else {
          car.attackTimer -= dt;
          if (car.attackTimer <= 0 && car.laneT >= 1) car.attackT = 0;
        }
        return view.busSpeed + r.creep;
      }
      case RivalPhase.Passed:
        if (car.brakeT > 0) {
          car.brakeT -= dt;
          return view.busSpeed * r.passBrake.to;
        }
        return view.busSpeed + r.leaveFaster;
      case RivalPhase.Retreat:
        return Math.max(0, view.busSpeed - 6);
    }
  }

  private beginLane(car: TrafficCar, lane: number, duration = this.deps.config.laneChangeDuration): void {
    if (lane === car.lane && car.laneT >= 1) return;
    car.fromX = car.pose.x;
    car.fromLane = car.currentLane;
    car.lane = lane;
    car.laneT = 0;
    car.laneDuration = duration;
    car.signal = lane < car.fromLane ? -1 : 1;
  }

  /** No other car of `car`'s direction in `lane` near `distance`. */
  private laneFree(lane: number, distance: number, car: TrafficCar): boolean {
    return !this.cars.some((o) => o !== car && !o.wrecked && o.lane === lane && Math.abs(o.pose.distance - distance) < (o.length + car.length) / 2 + 4);
  }

  /** The rival runs into oncoming traffic: both crash (the van's gain – BusManager). */
  private rivalMeetsOncoming(): void {
    for (const rival of this.cars) {
      if (rival.role !== TrafficRole.Rival || rival.wrecked) continue;
      for (const o of this.cars) {
        if (o.dir > 0 || o.wrecked) continue;
        const p = o.pose;
        if (!this.overlaps(rival, p.x - o.width / 2, p.x + o.width / 2, p.distance - o.length / 2, p.distance + o.length / 2, p.y, p.y + o.height)) continue;
        this.wreck(rival, p.x);
        this.wreck(o, rival.pose.x);
        this.happened.push({ incident: TrafficIncident.Crashed, car: rival });
        break;
      }
    }
  }

  private overlaps(car: TrafficCar, minX: number, maxX: number, minZ: number, maxZ: number, minY: number, maxY: number): boolean {
    const p = car.pose;
    if (p.x - car.width / 2 >= maxX || p.x + car.width / 2 <= minX) return false;
    if (p.distance - car.length / 2 >= maxZ || p.distance + car.length / 2 <= minZ) return false;
    return p.y < maxY && p.y + car.height > minY;
  }

  /** Follows the road height; off a ramp lip it flies at its climb rate (like the van). */
  private vertical(car: TrafficCar, dt: number): void {
    const p = car.pose;
    const ground = this.ground(p.distance, p.x);
    if (car.airborne) {
      car.vy -= this.deps.config.gravity * dt;
      p.y += car.vy * dt;
      if (p.y <= ground) {
        p.y = ground;
        car.vy = 0;
        car.airborne = false;
      }
      return;
    }
    if (ground < p.y - LIP && car.vy > 0) {
      car.airborne = true;
      p.y += car.vy * dt;
      return;
    }
    // at most a ramp's climb rate (sideways onto a ramp the height steps up at once – no launch)
    car.vy = dt > 0 ? Math.min((ground - p.y) / dt, car.speed * MAX_CLIMB) : 0;
    p.y = ground;
  }

  private ground(distance: number, x: number): number {
    return this.deps.groundHeightAt(distance, x) ?? this.deps.world.groundHeight;
  }

  /** Seconds until the chasing rival from behind reaches the van's tail, or null. */
  private secondsToReach(car: TrafficCar, view: TrafficView): number | null {
    if (!car.chasing || car.phase === RivalPhase.Alongside) return null;
    const gap = view.busDistance - view.busRear - (car.pose.distance + car.length / 2);
    if (gap < 0) return null;
    const closing = car.speed - view.busSpeed;
    if (closing <= 0.1) return car.phase === RivalPhase.Blocked ? 0 : null;
    return gap / closing;
  }

  private despawn(view: TrafficView): void {
    const c = this.deps.config;
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const car = this.cars[i]!;
      const rel = car.pose.distance - view.busDistance;
      // cars ahead turn off at an undecided crossing (the track behind it does not exist yet)
      const turnedOff = car.dir > 0 && car.pose.distance > view.roadEnd + 2;
      const passedGone = car.phase === RivalPhase.Passed && rel > 60;
      if (rel < -c.despawnBehind || rel > c.despawnAhead || turnedOff || passedGone) this.cars.splice(i, 1);
    }
  }
}
